import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import OrdersSubTabs from "./OrdersSubTabs";
import AuthService from "../../../api/services/AuthService";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import {
  MARGIN_SUB_TABS,
  MARGIN_SIDE_OPTIONS,
  MARGIN_ACCOUNT_TYPE_OPTIONS,
  buildMarginPairOptions,
} from "./marginOrderFilterConstants";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const ORDER_DESKTOP_COL_COUNT = 9;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatPair(raw) {
  if (!raw) return "—";
  if (String(raw).includes("/")) return raw;
  const known = ["USDT", "BTC", "ETH", "BNB", "USDC"];
  const s = String(raw).toUpperCase();
  for (const q of known) {
    if (s.endsWith(q) && s.length > q.length) return `${s.slice(0, -q.length)}/${q}`;
  }
  return raw;
}

/** Unwrap MongoDB $numberDecimal objects, then format. */
function dec(v) {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  return v;
}
function safeNum(v, decimals = 8) {
  const n = parseFloat(dec(v));
  if (!Number.isFinite(n)) return "—";
  return parseFloat(n.toFixed(decimals)).toString();
}

function toIsoFrom(dateStr) { return dateStr ? `${dateStr}T00:00:00.000Z` : undefined; }
function toIsoTo(dateStr) { return dateStr ? `${dateStr}T23:59:59.999Z` : undefined; }

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value}</span>
      <input ref={inputRef} type="date" className="so-dateCell__native" value={value}
        onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} />
    </div>
  );
}

const MarginOrders = () => {
  const initRange = spotActivityDefaultLocalRange();

  const [accountType, setAccountType] = useState("isolated");
  const [side, setSide] = useState("all");
  const [contract, setContract] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [pairOptions, setPairOptions] = useState([{ value: "all", label: "All" }]);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    AuthService.marginAccounts()
      .then((res) => {
        if (res?.success) {
          setRawAccounts(res.data || []);
          setPairOptions(buildMarginPairOptions(res.data || []));
        }
      })
      .catch(() => { });
  }, []);

  const resolvedPair = contract === "all"
    ? undefined
    : (() => {
      const acc = rawAccounts.find(
        (a) => `${a.base_asset}_${a.quote_asset}`.toLowerCase() === contract
      );
      return acc ? `${acc.base_asset}${acc.quote_asset}` : undefined;
    })();

  const loadRows = useCallback(async (pair, from, to) => {
    setLoading(true);
    try {
      const res = accountType === "cross"
        ? await AuthService.crossOpenOrders({ limit: 100 })
        : await AuthService.marginOpenOrders({ pair, start_date: toIsoFrom(from), end_date: toIsoTo(to), limit: 100 });
      if (res?.success) {
        setRows(
          (res.data || []).map((r) => ({
            id: r._id ?? r.order_id ?? r.id,
            time: formatDate(r.created_at ?? r.updated_at),
            pair: formatPair(r.pair),
            type: String(r.type ?? r.order_type ?? "—").toUpperCase(),
            side: String(r.side ?? "—").toUpperCase(),
            price: safeNum(r.price),
            amount: safeNum(r.quantity),
            filled: safeNum(r.filled_quantity),
            status: r.status ?? "—",
          }))
        );
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountType]);

  useEffect(() => {
    loadRows(resolvedPair, dateFrom, dateTo);
  }, [accountType, resolvedPair, dateFrom, dateTo, loadRows]);

  const handleCancel = async (orderId) => {
    if (!orderId) return;
    setCancellingId(orderId);
    try {
      const res = accountType === "cross"
        ? await AuthService.crossCancelOrder(orderId)
        : await AuthService.marginCancelOrder(orderId);
      if (res?.success) {
        setRows((prev) => prev.filter((r) => r.id !== orderId));
      }
    } catch { /* silent */ }
    finally { setCancellingId(null); }
  };

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  // Client-side side filter (pair + date are server-side)
  const filteredRows = rows.filter((r) => {
    if (side !== "all" && r.side?.toLowerCase() !== side) return false;
    return true;
  });

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setSide("all"); setContract("all"); setDateFrom(from); setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") { try { el.showPicker(); return; } catch { /**/ } }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter margin open orders">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown id={`${pref}moo-account`} label="Account" value={accountType} options={MARGIN_ACCOUNT_TYPE_OPTIONS}
            onChange={(v) => { setAccountType(v); setContract("all"); }} menuKey={`${pref}moo-account`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          <CustomDropdown id={`${pref}moo-side`} label="Side" value={side} options={MARGIN_SIDE_OPTIONS}
            onChange={setSide} menuKey={`${pref}moo-side`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          {accountType === "isolated" && (
            <CustomDropdown id={`${pref}moo-contract`} label="Contract" value={contract} options={pairOptions}
              onChange={setContract} menuKey={`${pref}moo-contract`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          )}
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          {loading
            ? <div className="so-emptyState__text">Loading…</div>
            : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="mo-open-title">
            <h1 id="mo-open-title" className="so-page__title">Open Orders</h1>
            <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Contract</th>
                    <th scope="col">Type</th>
                    <th scope="col">Side</th>
                    <th scope="col">Price</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Filled</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? renderEmptyBody() : pagedRows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{r.time}</td>
                      <td>{r.pair}</td>
                      <td>{r.type}</td>
                      <td><span className={r.side === "BUY" ? "so-side--buy" : "so-side--sell"}>{r.side}</span></td>
                      <td className="so-num">{r.price}</td>
                      <td className="so-num">{r.amount}</td>
                      <td className="so-num">{r.filled}</td>
                      <td><span className="so-badge--closed">{r.status}</span></td>
                      <td>
                        <button
                          type="button"
                          className="so-cancelBtn"
                          disabled={cancellingId === r.id}
                          onClick={() => handleCancel(r.id)}
                        >
                          {cancellingId === r.id ? "…" : "Cancel"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Open orders pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Open Orders</h2>
          <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-moo-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              {loading
                ? <div className="so-emptyState__text">Loading…</div>
                : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
            </div>
          ) : pagedRows.map((r) => (
            <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Order details">
              <div className="so-mobileCard__row"><span>Time</span><span>{r.time}</span></div>
              <div className="so-mobileCard__row"><span>Contract</span><span>{r.pair}</span></div>
              <div className="so-mobileCard__row"><span>Type</span><span>{r.type}</span></div>
              <div className="so-mobileCard__row"><span>Side</span><span className={r.side === "BUY" ? "so-side--buy" : "so-side--sell"}>{r.side}</span></div>
              <div className="so-mobileCard__row"><span>Price</span><span className="so-num">{r.price}</span></div>
              <div className="so-mobileCard__row"><span>Amount</span><span className="so-num">{r.amount}</span></div>
              <div className="so-mobileCard__row"><span>Filled</span><span className="so-num">{r.filled}</span></div>
              <div className="so-mobileCard__row"><span>Status</span><span className="so-badge--closed">{r.status}</span></div>
              <div className="so-mobileCard__row">
                <span>Action</span>
                <button type="button" className="so-cancelBtn" disabled={cancellingId === r.id}
                  onClick={() => handleCancel(r.id)}>
                  {cancellingId === r.id ? "…" : "Cancel"}
                </button>
              </div>
            </OrderMobileCardShell>
          ))}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Open orders pages"
          />
        </div>
      </div>
    </div>
  );
};

export default MarginOrders;
